import { useEffect, Fragment } from "react";

/* eslint-disable complexity */
import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { media } from "@atlaskit/primitives/responsive";
import { DN0 } from "@atlaskit/theme/colors";

import { Adg4Button } from "@/components/adg4/button/src";
import Link from "@/components/wpl/link/src";
import { AisSitesType } from "@/libraries/bxp-web-stargate-client/src/index";
import { Logo } from "@/libraries/wpl/foundations/src";

import { useAnalyticsClient } from "../../../../../analytics/wac-analytics/analytics-client";

import { FlareSvg } from "./flare-svg.tsx";

const START_URL =
    process.env["NODE_ENV"] === "production"
        ? "https://start.atlassian.com"
        : "https://start.stg.atlassian.com";

const wrapLogMeSecAndLines = css({
    "backgroundImage":
        "url(https://wac-cdn.atlassian.com/misc-assets/webp-images/Option C BG.svg)", // https://wac-cdn.atlassian.com/misc-assets/webp-images/Option D BG.svg
    "backgroundSize": "cover",
    "display": "flex",
    "alignItems": "center",
    "justifyContent": "center",
    "gap": "8%",
    "margin": "0 auto",
    [media.above.md]: {
        padding: "9.5rem 4rem",
        height: "685px"
    },
    [media.below.md]: {
        flexDirection: "column",
        gap: 48
    },
    "@media (min-width: 600px)": {
        padding: "48px 80px 120px"
    },
    "@media (max-width: 600px)": {
        padding: "48px 24px 120px"
    }
});

const logMeBackInSectionStyle = css({
    padding: "24px 0px 24px 0px",
    [media.above.sm]: {
        flexDirection: "row"
    },
    [media.above.md]: {
        maxWidth: "468px"
    },
    [media.below.md]: {
        width: "100%"
    }
});

const headingStyle = css({
    fontSize: "48px",
    lineHeight: "56px",
    fontWeight: "500",
    marginBlockStart: "0px",
    marginBlockEnd: "8px"
});

const subHeadingStyle = css({
    fontSize: "20px",
    marginBlockEnd: "24px",
    marginBlockStart: "0px"
});

const confluenceSiteBoxContainerStyle = css({
    position: "relative"
});

const flareContainerCss = css({
    "position": "absolute",
    "insetInlineStart": -64,
    "zIndex": 2,
    "@media (max-width: 600px)": {
        display: "none"
    }
});

const confluenceSiteBoxUnderlayStyle = css({
    "width": "80%",
    "position": "absolute",
    "insetBlockEnd": -18,
    "insetInlineEnd": -14,
    "background": "#FCA700",
    "height": 60,
    "@media (max-width: 600px)": {
        height: 104
    }
});

const confluenceSiteBoxStyle = css({
    "position": "relative",
    "zIndex": 1,
    "width": "auto",
    "height": "72px",
    "display": "flex",
    "justifyContent": "space-between",
    "flexDirection": "row",
    "alignItems": "center",
    "border": `solid 1px ${DN0}`,
    "borderRadius": 4,
    "padding": "16px 24px 16px 16px",
    "backgroundColor": "#ffff",
    "@media (max-width: 600px)": {
        flexDirection: "column",
        height: "auto",
        alignItems: "start",
        padding: "7px 16px 16px"
    }
});

const logoAndSiteNameStyle = css({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    minWidth: "0px"
});

const logoStyle = css({
    marginInlineEnd: "10px",
    marginBlockStart: "9px"
});

const siteStyle = css({
    lineHeight: "22px",
    fontSize: "16px",
    overflow: "hidden"
});

const productStyle = css({
    fontWeight: "500",
    height: "22px",
    fontSize: "17px",
    textAlign: "start"
});

const siteNameStyle = css({
    fontWeight: "300",
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap",
    height: "22px"
});

const otherSiteLinkStyle = css({
    marginBlockStart: "48px"
});

const buttonStyle = css({
    "whiteSpace": "nowrap",
    "marginInlineStart": "15px",
    "@media (max-width: 600px)": {
        marginInlineStart: 0,
        width: "100%",
        maxWidth: "100%"
    }
});

const imgWrapperStyle = css({
    [media.above.md]: {
        maxWidth: "650px"
    },
    [media.below.md]: {
        width: "100%"
    }
});

const imgStyle = css({
    width: "100%"
});

export enum TRACKING_EVENT_NAMES {
    loggedInHeroProductCtaClicked = "loggedInHeroProductCtaClicked",
    loggedInHeroAnotherSiteCtaClicked = "loggedInHeroAnotherSiteCtaClicked"
}

export const loggedInHeroAnalyticsPayload = {
    eventType: "ui",
    action: "clicked",
    actionSubject: "button",
    actionSubjectId: "loggedInHero",
    source: "/software/confluence"
};

export const loggedInHeroLoadedAnalyticsPayload = {
    eventType: "ui",
    action: "loaded",
    actionSubject: "page",
    actionSubjectId: "loggedInHero",
    source: "/software/confluence",
    attributes: {
        atlasId: "CFIND-1849"
    }
};

interface LogMeInSectionProps {
    confluenceSites: AisSitesType[];
    userProfileName: string | undefined;
}

export const LogMeInSection = ({
    confluenceSites,
    userProfileName
}: LogMeInSectionProps) => {
    const createConfluenceUrl = (url: string) => {
        try {
            const tempUrl = new URL(url);
            tempUrl.pathname = "/wiki";
            tempUrl.search = "";
            return tempUrl.toString();
        } catch {
            return url;
        }
    };

    const getSitenameFromUrl = (url: string) => {
        try {
            const tempUrl = new URL(url);
            return tempUrl.hostname.split(".")[0] || "";
        } catch {
            return "";
        }
    };

    const firstName = userProfileName?.split(" ")[0];
    const siteUrl =
        createConfluenceUrl(confluenceSites[0]?.url || "") || START_URL;
    const siteName = getSitenameFromUrl(confluenceSites[0]?.url || "");
    const hasMultipleConfluences = confluenceSites.length > 1;

    const analyticsClient = useAnalyticsClient();
    const { createAnalyticsEvent } = useAnalyticsEvents();

    useEffect(() => {
        if (typeof window !== "undefined") {
            createAnalyticsEvent({
                ...loggedInHeroLoadedAnalyticsPayload
            }).fire();
        }
    }, [analyticsClient, createAnalyticsEvent]);

    const clickHandler = (eventName: TRACKING_EVENT_NAMES) => {
        createAnalyticsEvent({
            ...loggedInHeroAnalyticsPayload,
            attributes: {
                eventName,
                url: window.location.href,
                atlasId: "CFIND-1849"
            }
        }).fire();
    };

    const intl = useIntl();

    return (
        <Fragment>
            <section>
                <div css={wrapLogMeSecAndLines}>
                    <div css={logMeBackInSectionStyle}>
                        <h1 css={headingStyle}>
                            {firstName ? (
                                <FormattedMessage
                                    id="ConfluenceSoftwarePageTemplate.loggedInHero.welcomeBackWithName"
                                    defaultMessage={`Welcome back, {name}.`}
                                    values={{ name: firstName }}
                                />
                            ) : (
                                <FormattedMessage
                                    id="ConfluenceSoftwarePageTemplate.loggedInHero.welcomeBack"
                                    defaultMessage="Welcome back"
                                />
                            )}
                        </h1>
                        <p css={subHeadingStyle}>
                            <FormattedMessage
                                id="ConfluenceSoftwarePageTemplate.loggedInHero.pickUpWhereYouLeftOff"
                                defaultMessage="Pick up where you left off in Confluence."
                            />
                        </p>

                        <div css={confluenceSiteBoxContainerStyle}>
                            <div css={flareContainerCss}>
                                <FlareSvg />
                            </div>
                            <div css={confluenceSiteBoxStyle}>
                                <div css={logoAndSiteNameStyle}>
                                    <div css={logoStyle}>
                                        <Logo
                                            brand="confluence"
                                            type="mark"
                                            appearance="brand"
                                        />
                                    </div>
                                    <div css={siteStyle}>
                                        <div css={productStyle}>Confluence</div>
                                        <div css={siteNameStyle}>
                                            {siteName}
                                        </div>
                                    </div>
                                </div>
                                <Adg4Button
                                    id="confluence-button"
                                    variant="primary"
                                    size="small"
                                    color="blue"
                                    css={buttonStyle}
                                    href={siteUrl}
                                    onClick={() =>
                                        clickHandler(
                                            TRACKING_EVENT_NAMES.loggedInHeroProductCtaClicked
                                        )
                                    }
                                >
                                    <FormattedMessage
                                        id="ConfluenceSoftwarePageTemplate.loggedInHero.goToConfluence"
                                        defaultMessage="Go to Confluence"
                                    />
                                </Adg4Button>
                            </div>
                            <div css={confluenceSiteBoxUnderlayStyle} />
                        </div>

                        {hasMultipleConfluences && (
                            <Link
                                href={START_URL}
                                text={intl.formatMessage({
                                    id: "ConfluenceSoftwarePageTemplate.loggedInHero.lookingForDifferentSite",
                                    defaultMessage:
                                        "Looking for a different site?"
                                })}
                                onClick={() =>
                                    clickHandler(
                                        TRACKING_EVENT_NAMES.loggedInHeroAnotherSiteCtaClicked
                                    )
                                }
                                css={otherSiteLinkStyle}
                            />
                        )}
                    </div>
                    <div css={imgWrapperStyle}>
                        <img
                            alt=""
                            src="https://wac-cdn.atlassian.com/misc-assets/webp-images/Hero Image B-1884788674.webp"
                            // src="https://wac-cdn.atlassian.com/misc-assets/webp-images/Hero Image-4202905184.webp"
                            css={imgStyle}
                        />
                    </div>
                </div>
            </section>
        </Fragment>
    );
};

export default LogMeInSection;
